import React, { useEffect, useState } from 'react';

import { ChevronLeft, ChevronRight, Star } from 'lucide-react';

interface Testimonial {
  id: string;
  name: string;
  position: string;
  company: string;
  content: string;
  rating: number;
  image?: string;
}

const TestimonialCarousel: React.FC = () => {
  // Datos de ejemplo para testimonios
  const testimonials: Testimonial[] = [
    {
      id: '1',
      name: 'Carlos Mendoza',
      position: 'Director de Operaciones',
      company: 'Industrias Globales S.A.',
      content:
        'La implementación del sistema ERP transformó completamente nuestra operación. Redujimos tiempos de proceso en un 40% y mejoramos la precisión de nuestros datos. El equipo de SOL IN PRIME JC entendió perfectamente nuestras necesidades.',
      rating: 5,
      image: '/images/testimonials/testimonial-1.jpg',
    },
    {
      id: '2',
      name: 'María Fernández',
      position: 'CEO',
      company: 'TechSolutions Chile',
      content:
        'El desarrollo web que realizaron para nuestra empresa superó todas nuestras expectativas. No solo es visualmente impresionante, sino que las conversiones aumentaron un 35% en los primeros dos meses. Altamente recomendados.',
      rating: 5,
      image: '/images/testimonials/testimonial-2.jpg',
    },
    {
      id: '3',
      name: 'Javier Rojas',
      position: 'Director de Transformación Empresarial',
      company: 'Grupo Financiero Andino',
      content:
        'La consultoría en transformación empresarial nos dio claridad sobre nuestra hoja de ruta tecnológica. Implementamos las recomendaciones y hemos visto mejoras significativas en eficiencia y satisfacción del cliente.',
      rating: 4,
      image: '/images/testimonials/testimonial-3.jpg',
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;

    if (isAutoPlaying) {
      interval = setInterval(() => {
        setCurrentIndex(prevIndex => (prevIndex + 1) % testimonials.length);
      }, 5000);
    }

    return () => clearInterval(interval);
  }, [isAutoPlaying, testimonials.length]);

  const handlePrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex(prevIndex => (prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1));
  };

  const handleNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex(prevIndex => (prevIndex + 1) % testimonials.length);
  };

  const renderStars = (rating: number) => {
    return Array.from({ length: 5 }).map((_, index) => (
      <Star
        key={index}
        className={`h-5 w-5 ${index < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`}
      />
    ));
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="relative overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 bg-blue-50">
          <h3 className="text-xl font-bold text-gray-800">Lo que dicen nuestros clientes</h3>
          <div className="flex space-x-2">
            <button
              onClick={handlePrev}
              className="p-2 rounded-full bg-white shadow hover:bg-gray-100 transition-colors"
              aria-label="Testimonio anterior"
            >
              <ChevronLeft className="h-5 w-5 text-blue-600" />
            </button>
            <button
              onClick={handleNext}
              className="p-2 rounded-full bg-white shadow hover:bg-gray-100 transition-colors"
              aria-label="Testimonio siguiente"
            >
              <ChevronRight className="h-5 w-5 text-blue-600" />
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden">
          <div
            className="flex transition-transform duration-500 ease-in-out"
            style={{ transform: `translateX(-${currentIndex * 100}%)` }}
          >
            {testimonials.map(testimonial => (
              <div key={testimonial.id} className="w-full flex-shrink-0 p-6">
                <div className="flex flex-col md:flex-row gap-6 items-center">
                  {testimonial.image && (
                    <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="flex-1">
                    <div className="flex mb-2">{renderStars(testimonial.rating)}</div>
                    <blockquote className="text-gray-700 italic mb-4">
                      {testimonial.content}
                    </blockquote>
                    <div>
                      <p className="font-bold text-gray-900">{testimonial.name}</p>
                      <p className="text-gray-600 text-sm">
                        {testimonial.position}, {testimonial.company}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TestimonialCarousel;
